import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { isWageIncomeType } from "@perdiem/trpc/income-types";
import { estimateSocialSecurity } from "@perdiem/trpc/social-security";
import { trpc } from "../lib/trpc";
import { webPrivacyProps } from "../lib/privacy";
import { formatCurrency } from "../lib/utils";
import { Button } from "./Button";
import { Card } from "./Card";
import { FormErrorBanner } from "./FormErrorBanner";
import { CurrencyInput } from "./CurrencyInput";
import { TextInput } from "./TextInput";

export type SocialSecurityEstimatorProfile = {
  id: string;
  type: "primary" | "spouse";
  firstName: string | null;
  lastName: string | null;
  birthYear: number | null;
};

export type SocialSecurityEstimatorIncomeStream = {
  type: string;
  annualAmount: number;
  personId: string | null;
};

type SocialSecurityEstimateResult = {
  personId: string;
  personName: string;
  earlyMonthly: number;
  fraMonthly: number;
  lateMonthly: number;
  fullRetirementAge: number;
  fraYear: number;
};

type SocialSecurityEstimatorProps = {
  profiles: SocialSecurityEstimatorProfile[];
  incomeStreams?: SocialSecurityEstimatorIncomeStream[];
  onEstimateCreated?: () => void;
  mode?: "button" | "auto";
  optedOut?: boolean;
  onOptOut?: () => void;
  onUndoOptOut?: () => void;
};

export type SocialSecurityEstimatePersonInput = {
  personId: string;
  currentSalary: number;
};

function getProfileLabel(
  profile: SocialSecurityEstimatorProfile,
  totalProfiles: number,
) {
  const name = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  if (totalProfiles === 1) return "You";
  return profile.type === "primary" ? "Primary" : "Spouse";
}

function getMissingBirthYearMessage(
  profile: SocialSecurityEstimatorProfile,
  totalProfiles: number,
) {
  const label = getProfileLabel(profile, totalProfiles);
  if (label === "You") {
    return "Your profile is missing a birth year. Update your profile before estimating Social Security.";
  }
  return `${label} is missing a birth year. Update their profile before estimating Social Security.`;
}

function buildDefaultSalaries(args: {
  profiles: SocialSecurityEstimatorProfile[];
  incomeStreams: SocialSecurityEstimatorIncomeStream[];
}) {
  const assignedWagesByPersonId: Record<string, number> = {};
  args.profiles.forEach((profile) => {
    assignedWagesByPersonId[profile.id] = 0;
  });

  let unassignedWages = 0;
  args.incomeStreams.forEach((stream) => {
    if (!isWageIncomeType(stream.type)) return;

    if (
      stream.personId &&
      assignedWagesByPersonId[stream.personId] !== undefined
    ) {
      assignedWagesByPersonId[stream.personId] += Number(
        stream.annualAmount ?? 0,
      );
      return;
    }

    unassignedWages += Number(stream.annualAmount ?? 0);
  });

  const values: Record<string, string> = {};
  args.profiles.forEach((profile) => {
    const assigned = assignedWagesByPersonId[profile.id] ?? 0;
    const defaultAnnualSalary =
      args.profiles.length === 1 ? assigned + unassignedWages : assigned;
    values[profile.id] =
      defaultAnnualSalary > 0 ? String(Math.round(defaultAnnualSalary)) : "";
  });

  return {
    values,
    hasUnassignedWages: args.profiles.length > 1 && unassignedWages > 0,
  };
}

export function buildSocialSecurityEstimatePeople(args: {
  profiles: SocialSecurityEstimatorProfile[];
  incomeStreams: SocialSecurityEstimatorIncomeStream[];
}): SocialSecurityEstimatePersonInput[] {
  const defaults = buildDefaultSalaries(args);
  const visibleProfiles = args.profiles.filter(
    (profile) => profile.type === "primary" || profile.type === "spouse",
  );

  return visibleProfiles.flatMap((profile) => {
    const rawSalary = defaults.values[profile.id]?.trim() ?? "";
    if (!rawSalary || profile.birthYear == null) return [];

    const parsedSalary = parseFloat(rawSalary);
    if (!parsedSalary || Number.isNaN(parsedSalary) || parsedSalary <= 0) {
      return [];
    }

    return [{ personId: profile.id, currentSalary: parsedSalary }];
  });
}

function buildSocialSecurityPreviewEstimates(args: {
  profiles: SocialSecurityEstimatorProfile[];
  incomeStreams: SocialSecurityEstimatorIncomeStream[];
}): SocialSecurityEstimateResult[] {
  const people = buildSocialSecurityEstimatePeople(args);
  const profilesById = new Map(args.profiles.map((profile) => [profile.id, profile]));
  const visibleProfileCount = args.profiles.filter(
    (profile) => profile.type === "primary" || profile.type === "spouse",
  ).length;

  return people.flatMap((person) => {
    const profile = profilesById.get(person.personId);
    if (!profile?.birthYear) return [];

    return [
      {
        personId: person.personId,
        personName: getProfileLabel(profile, visibleProfileCount),
        ...estimateSocialSecurity(person.currentSalary, profile.birthYear),
      },
    ];
  });
}

export function SocialSecurityEstimator({
  profiles,
  incomeStreams = [],
  onEstimateCreated,
  mode = "button",
  optedOut = false,
  onOptOut,
  onUndoOptOut,
}: SocialSecurityEstimatorProps) {
  const utils = trpc.useUtils();
  const estimateMutation =
    trpc.income.createSocialSecurityEstimates.useMutation();

  const [expanded, setExpanded] = useState(false);
  const [salaryByPersonId, setSalaryByPersonId] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SocialSecurityEstimateResult[] | null>(
    null,
  );

  const defaults = buildDefaultSalaries({ profiles, incomeStreams });
  const visibleProfiles = profiles.filter(
    (profile) => profile.type === "primary" || profile.type === "spouse",
  );
  const previewEstimates = buildSocialSecurityPreviewEstimates({
    profiles,
    incomeStreams,
  });

  const resetEstimator = () => {
    setExpanded(false);
    setSalaryByPersonId({});
    setError(null);
    setResult(null);
  };

  const handleEstimate = async () => {
    setError(null);

    try {
      const people = visibleProfiles.flatMap((profile) => {
        const rawSalary = (
          salaryByPersonId[profile.id] ??
          defaults.values[profile.id] ??
          ""
        ).trim();
        if (!rawSalary) return [];

        const parsedSalary = parseFloat(rawSalary);
        if (!parsedSalary || Number.isNaN(parsedSalary) || parsedSalary <= 0) {
          throw new Error(
            `Enter a valid annual salary for ${getProfileLabel(profile, visibleProfiles.length)}.`,
          );
        }

        if (profile.birthYear == null) {
          throw new Error(
            getMissingBirthYearMessage(profile, visibleProfiles.length),
          );
        }

        return [
          {
            personId: profile.id,
            currentSalary: parsedSalary,
          },
        ];
      });

      if (people.length === 0) {
        setError(
          "Enter at least one current annual salary to estimate Social Security.",
        );
        return;
      }

      const res = await estimateMutation.mutateAsync({ people });
      setResult(res?.estimates ?? []);

      await Promise.all([
        utils.income.list.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);

      onEstimateCreated?.();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not estimate Social Security benefits. Please try again.",
      );
    }
  };

  if (mode === "auto") {
    if (optedOut) {
      return (
        <View className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Social Security estimate removed
          </Text>
          {onUndoOptOut ? (
            <Pressable onPress={onUndoOptOut} className="mt-1 self-start">
              <Text className="text-xs font-medium text-green-700 dark:text-green-400">
                Restore estimate
              </Text>
            </Pressable>
          ) : null}
        </View>
      );
    }

    if (visibleProfiles.length === 0 || previewEstimates.length === 0) {
      return null;
    }

    const totalFraMonthly = previewEstimates.reduce(
      (sum, entry) => sum + entry.fraMonthly,
      0,
    );

    return (
      <Card>
        <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Social Security estimate
        </Text>
        <Text className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Per Diem will add this future income when you continue.
        </Text>
        <View className="rounded-xl bg-green-50 dark:bg-green-900/20 px-4 py-3 gap-3">
          {previewEstimates.map((entry) => (
            <View key={entry.personId} className="gap-1.5">
              <Text
                className="text-sm font-semibold text-green-800 dark:text-green-200"
                {...webPrivacyProps('private')}
              >
                {entry.personName}
              </Text>
              <View className="flex-row justify-between">
                <Text className="text-xs text-green-700 dark:text-green-300">
                  Age 62
                </Text>
                <Text className="text-xs font-semibold text-green-800 dark:text-green-200" {...webPrivacyProps('financial')}>
                  {formatCurrency(entry.earlyMonthly)}/mo
                </Text>
              </View>
              <View className="flex-row justify-between">
                <Text className="text-sm text-green-800 dark:text-green-200 font-medium">
                  Age {entry.fullRetirementAge}
                </Text>
                <Text className="text-sm font-bold text-green-900 dark:text-green-100" {...webPrivacyProps('financial')}>
                  {formatCurrency(entry.fraMonthly)}/mo
                </Text>
              </View>
              <View className="flex-row justify-between">
                <Text className="text-xs text-green-700 dark:text-green-300">
                  Age 70
                </Text>
                <Text className="text-xs font-semibold text-green-800 dark:text-green-200" {...webPrivacyProps('financial')}>
                  {formatCurrency(entry.lateMonthly)}/mo
                </Text>
              </View>
            </View>
          ))}
        </View>
        <Text className="text-xs text-gray-400 dark:text-gray-500 text-center mt-3" {...webPrivacyProps('financial')}>
          Estimated {formatCurrency(totalFraMonthly)}/mo total at full retirement age.
        </Text>
        {onOptOut ? (
          <Pressable onPress={onOptOut} className="mt-3 self-center">
            <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Remove this estimate
            </Text>
          </Pressable>
        ) : null}
      </Card>
    );
  }

  if (!expanded) {
    return (
      <Button variant="ghost" onPress={() => setExpanded(true)}>
        Estimate Social Security
      </Button>
    );
  }

  if (visibleProfiles.length === 0) {
    return (
      <Card>
        <Text className="text-sm font-semibold text-gray-700 mb-1">
          Social Security Estimator
        </Text>
        <Text className="text-sm text-gray-500 mb-4">
          Add a household member before estimating Social Security.
        </Text>
        <Button variant="ghost" onPress={resetEstimator}>
          Close
        </Button>
      </Card>
    );
  }

  const totalFraMonthly =
    result?.reduce((sum, entry) => sum + entry.fraMonthly, 0) ?? 0;

  return (
    <Card>
      <Text className="text-sm font-semibold text-gray-700 mb-1">
        Social Security Estimator
      </Text>
      <Text className="text-xs text-gray-400 mb-4">
        Estimate each person&apos;s future Social Security benefit from their
        current earnings.
      </Text>

      <FormErrorBanner error={error} onDismiss={() => setError(null)} />
      {error && <View className="mb-3" />}

      {result ? (
        <View className="gap-3">
          <View className="rounded-xl bg-green-50 px-4 py-3">
            <Text className="text-xs font-medium text-green-700 mb-2">
              Estimated monthly benefits added to your income
            </Text>
            <View className="gap-3">
              {result.map((entry) => (
                <View key={entry.personId} className="gap-1.5">
                  <Text
                    className="text-sm font-semibold text-green-800"
                    {...webPrivacyProps('private')}
                  >
                    {entry.personName}
                  </Text>
                  <View className="flex-row justify-between">
                    <Text className="text-xs text-green-600">
                      Age 62 (early)
                    </Text>
                    <Text className="text-xs font-semibold text-green-700" {...webPrivacyProps('financial')}>
                      {formatCurrency(entry.earlyMonthly)}/mo
                    </Text>
                  </View>
                  <View className="flex-row justify-between">
                    <Text className="text-sm text-green-700 font-medium">
                      Age {entry.fullRetirementAge} (full retirement)
                    </Text>
                    <Text className="text-sm font-bold text-green-800" {...webPrivacyProps('financial')}>
                      {formatCurrency(entry.fraMonthly)}/mo
                    </Text>
                  </View>
                  <View className="flex-row justify-between">
                    <Text className="text-xs text-green-600">
                      Age 70 (delayed)
                    </Text>
                    <Text className="text-xs font-semibold text-green-700" {...webPrivacyProps('financial')}>
                      {formatCurrency(entry.lateMonthly)}/mo
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
          <Text className="text-xs text-gray-400 text-center" {...webPrivacyProps('financial')}>
            Added {formatCurrency(totalFraMonthly)}/mo total at full retirement
            age, with 2% annual COLA.
          </Text>
          <Button variant="secondary" size="sm" onPress={resetEstimator}>
            Done
          </Button>
        </View>
      ) : (
        <View className="gap-4">
          {defaults.hasUnassignedWages && (
            <View className="rounded-xl bg-amber-50 px-4 py-3">
              <Text className="text-xs text-amber-700">
                Existing wage streams are not linked to a specific person, so
                each salary needs to be entered manually.
              </Text>
            </View>
          )}

          {visibleProfiles.map((profile) => {
            const label = getProfileLabel(profile, visibleProfiles.length);
            const rawValue =
              salaryByPersonId[profile.id] ?? defaults.values[profile.id] ?? "";

            return (
              <View key={profile.id}>
                <CurrencyInput
                  label={`${label} annual salary (gross)`}
                  value={rawValue}
                  onChangeText={(text) => {
                    setSalaryByPersonId((current) => ({
                      ...current,
                      [profile.id]: text,
                    }));
                    setError(null);
                  }}
                  placeholder="e.g. 85,000"
                  hint={
                    profile.birthYear == null
                      ? "Birth year is missing on this profile, so the estimate cannot be created until that is filled in."
                      : undefined
                  }
                />
              </View>
            );
          })}

          <View className="flex-row gap-2">
            <Button
              className="flex-1"
              onPress={handleEstimate}
              loading={estimateMutation.isPending}
            >
              Estimate
            </Button>
            <Button variant="ghost" onPress={resetEstimator}>
              Cancel
            </Button>
          </View>
        </View>
      )}
    </Card>
  );
}
